import { combineEpics, ofType } from "redux-observable";
import {
    catchError,
    from,
    map,
    mergeMap,
    of,
    skipWhile,
    switchMap,
    withLatestFrom,
} from "rxjs";
import {
    setBopd,
    setBopdValues,
    setBubblePoint,
    setBwpd,
    setFluidViscosity,
    setGasCompressibility,
    setGasProduction2,
    setGLR2,
    setGOR2,
    setOilAPI2,
    setOilCut,
    setSpGrOil2,
    setWaterCutCombinedSpGr,
} from "../../../store/jetlift/jetlift-input/FluidSlice";
import { RootState } from "../../../store/ConfigureStore"; // Ensure this is the correct import
import {
    getBubblePoint,
    getBWPDvalue,
    getFluidViscosityChange,
    getGasCalData,
    getGasCompressValue,
    getOilAPI,
    getSpGrOil,
    getWateCutOilCut,
    getWaterCutCombinedSpGr,
} from "../../../services/jetlift/jetlift-input/fluid.service";
import { bubblePointPressure, fluidViscosityPayload, gasCompressPayload } from "../../../store/model/jetlift/jetlift";

const fetchWaterCutChangeOilCut = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/fluidTab/setWaterCut"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput))
        ),
        switchMap(([actionData, fluidInput]) => {
            return from(getWateCutOilCut(+fluidInput?.waterCut)).pipe(
                map((response) => {
                    return setOilCut(Number(response));
                }),
                catchError((error) => {
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const fetchCombinedSpGrEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/fluidTab/setRegion2",
            "jetlift/fluidTab/setSpGrWater",
            "jetlift/fluidTab/setSpGrOil",
            "jetlift/fluidTab/setSpGrOil2",
            "jetlift/fluidTab/setBwpd",
            "jetlift/fluidTab/setBopd",
            "jetlift/fluidTab/setGOR",
            "jetlift/fluidTab/setGOR2",
            "jetlift/fluidTab/setSpGrGas"
        ),
        withLatestFrom(
            state$?.pipe(
                map((state: RootState) => state?.jetlift?.well?.wellInput)
            ),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.region)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.regionList)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(
            ([
                action,
                tab2Input,
                fluidData,
                region,
                regionList,
                unit,
            ]) => {
                const selectedRegion = regionList?.find((x: any) => x?.value === region);
                const BOPD = +fluidData?.bopd || 0;
                const BWPD = +fluidData?.bwpd || 0;
                const bottomHoleTemp = +fluidData?.bottomHoleTemp || 0;
                let GOR = +fluidData?.gor || 0;
                let gasCompression = +fluidData?.gasCompressibility || 0;
                const spGrGas = +fluidData?.spGrGas || 0;
                const spGrWater = +fluidData?.spGrWater || 0;
                const spGrOil = +fluidData?.spGrOil || 0;
                const oilAPI = +fluidData?.oilAPI || 0;
                const BOPDUnit = +unit?.unitBOPDListVal || 0;
                const BWPDUnit = +unit?.unitBWPDListVal || 0;
                const bottomHoleTempUnit = +unit?.unitBottomHoleTempListVal || 0;
                const gasAvailability = +fluidData?.gasAvailblty || 0;
                const GORUnit = +unit?.unitGORListVal || 0;
                const casingPressure =
                    tab2Input?.casingPressure ?? 0;
                const casingPressureUnit = +unit?.unitCasingPressureListVal || 0;
                const flop = +tab2Input?.flop || 0;
                const bubblePressure = +fluidData?.bubblePoint || 0;
                const bubblePressureUnit = +unit?.unitBubblePointListVal || 0;
                const gasProduction = fluidData?.gasProduction || 0;
                const gasProductionUnit = +unit?.unitGasProductionListVal || 0;
                const flopUnit = +unit?.unitFLOPListVal || 0;
                const regionName = selectedRegion?.label ?? "";
                if (gasAvailability === 0) {
                    GOR = 0;
                    gasCompression = 0;
                }
                const compositeSpGrInputs = {
                    SpGrOil: spGrOil,
                    SpGrWater: spGrWater,
                    OilCut: +fluidData?.oilCut || 0,
                    WaterCut: +fluidData?.waterCut || 0,
                    BWPD: BWPD,
                    BWPDUnit: BWPDUnit,
                    OilAPI: oilAPI,
                    BOPD: BOPD,
                    BOPDUnit: BOPDUnit,
                    SpGrGas: spGrGas,
                    GOR: GOR,
                    GORUnit: GORUnit,
                    BottomHoleTemp: bottomHoleTemp,
                    BottomHoleTempUnit: bottomHoleTempUnit,
                    bubblePointPressure: bubblePressure,
                    bubblePointPressureUnit: bubblePressureUnit,
                    FLOP: flop,
                    FLOPUnit: flopUnit,
                    CasingPressure: casingPressure || 0,
                    CasingPressureUnit: casingPressureUnit,
                    IsGas: gasAvailability === 1 ? true : false,
                    GasCompressibility: gasCompression,
                    GasProduction: gasProduction,
                    GasProductionUnit: gasProductionUnit,
                    RegionName: regionName,
                };
                return from(getWaterCutCombinedSpGr(compositeSpGrInputs)).pipe(
                    map((response) => {
                        return setWaterCutCombinedSpGr(response);
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                    })
                );
            }
        )
    );
};

const fetchBubblePoint = (actions$: any, state$: any) => {
    return actions$.pipe(
        ofType(
            "jetlift/fluidTab/fetchBubblePoint",
            "jetlift/fluidTab/setBottomHoleTemp",
            "jetlift/fluidTab/setGOR",
            "jetlift/fluidTab/setOilAPI",
            "jetlift/fluidTab/setSpGrGas"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
        ),
        switchMap(([actionData, fluidInput]) => {
            const postData = {
                reservoirTempF: +fluidInput?.bottomHoleTemp || 0,
                GOR: +fluidInput?.gor || 0,
                oilAPI: +fluidInput?.oilAPI || 0,
                gasSpecificGravity: +fluidInput?.spGrGas || 0,
            } as bubblePointPressure;
            return from(getBubblePoint(postData)).pipe(
                map((data) => {
                    if (fluidInput?.spGrGas === 0) {
                        return setBubblePoint(0);
                    } else {
                        if (data) {
                            return setBubblePoint(data);
                        }
                        return setBubblePoint(fluidInput?.bubblePoint);
                    }
                }),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const GetOilApiChange = (actions$: any, state$: any) => {
    return actions$.pipe(
        ofType(
            "jetlift/fluidTab/setOilAPI", 
            // "jetlift/fluidTab/setFluidTabDetails"
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput))
        ),
        switchMap(([actionData, fluidInput]) => {
            const gasinputApi = +fluidInput?.oilAPI || 0;
            if (!gasinputApi) {
                return [];
            }
            return from(getOilAPI(gasinputApi)).pipe(
                map((data) => {
                    return setSpGrOil2(Number(data));
                }),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const GetFluidViscosity = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/fluidTab/fetchFluidViscosity",
            "jetlift/fluidTab/setWaterCut",
            "jetlift/fluidTab/setBottomHoleTemp",
            "jetlift/fluidTab/setGOR",
            "jetlift/fluidTab/setGOR2",
            "jetlift/fluidTab/setOilAPI",
            "jetlift/fluidTab/setSpGrGas",
            "jetlift/well/setDesiredProductionRate",
            "jetlift/well/setPumpSuctionPressure",
            "jetlift/fluidTab/setFluidTabDetails",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
        ),
        skipWhile(([actionData, fluidInput]) => {
            return actionData.type !== "jetlift/fluidTab/fetchFluidViscosity" && (+fluidInput?.fluidViscosity > 0 || +fluidInput?.gor <= 0 || +fluidInput?.oilAPI <= 0 || +fluidInput?.bottomHoleTemp <= 0)
        }),
        switchMap(([actionData, wellInput, fluidInput]) => {
            const GOR = fluidInput?.gor || 0;
            const BottomHoleTemperature = +fluidInput?.bottomHoleTemp || 0;
            const OilApi = +fluidInput?.oilAPI || 0;
            const ViscosityData = {
                pumpSuctionPressure: +wellInput?.desiredPumpSuctionPressure || 0,
                gor: +GOR, 
                reservoirTemp: BottomHoleTemperature, 
                oilAPI: OilApi, 
                waterCut: +fluidInput?.waterCut || 0, 
                gasSpecificGravity: +fluidInput?.spGrGas || 0,
                flowRateDesired: +wellInput?.productionRate || 0,
            } as fluidViscosityPayload;
            return from(getFluidViscosityChange(ViscosityData)).pipe(
                map((response) => {
                    if (response && String(response) === "Infinity") {
                        return setFluidViscosity("");
                    }
                    return setFluidViscosity(Number(response));
                }),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const GetSpGrOilChanges = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/fluidTab/setSpGrOil"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput))
        ),
        switchMap(([actionData, fluidInput]) => {
            return from(getSpGrOil(+fluidInput?.spGrOil)).pipe(
                map((response) => {
                    return setOilAPI2(Number(response));
                }),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const gasProdChange = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/fluidTab/setWaterCut",
            "jetlift/well/setDesiredProductionRate",
            "jetlift/fluidTab/setGasAvailability",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, tab2Input, fluidInput, unit]) => {
            const postData = {
                BOPDUnit: +unit?.unitBOPDListVal || 0,
                BWPDUnit: +unit?.unitBWPDListVal || 0,
                ProductionRate: +tab2Input.productionRate || 0,
                ProductionRateUnit: +unit?.unitProductionRateListVal || 0,
                WaterCut: +fluidInput?.waterCut || 0
            }
            return from(getBWPDvalue(postData)).pipe(
                mergeMap((response) => [
                    setBopdValues(response),
                    setBopd(Number(response?.bopd)),
                    setBwpd(Number(response?.bwpd))
                ]),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            );
        })
    );
};

const gasCalDataGor = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/fluidTab/setGOR"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, tab2Input, fluidInput, unit]) => {
            let postData = {
                BOPD: +fluidInput?.bopd || 0,
                inputval: +fluidInput?.gor || 0,
                BOPDUnit: +unit?.unitBOPDListVal || 0,
                inputvalUnit: +unit?.unitGORListVal || 0,
                GLRUnit: +unit?.unitGLRListVal || 0,
                GORUnit: +unit?.unitGORListVal || 0,
                GasProductionUnit: +unit?.unitGasProductionListVal || 0,
                WaterCut: +fluidInput?.waterCut || 0,
                Productionrate: +tab2Input.productionRate || 0,
                productionrateUnit: +unit?.unitProductionRateListVal || 0,
                type: 1
            }
            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGasProduction2(Number(data?.gasProduction)),
                    setGLR2(Number(data?.glr)),
                    { type: 'jetlift/fluidTab/fetchBubblePoint' },
                ]),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            )
        })
    );
};

const gasCalDataGasProduction = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/fluidTab/setGasProduction"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, tab2Input, fluidInput, unit]) => {
            let postData = {
                BOPD: +fluidInput?.bopd || 0,
                inputval: +fluidInput?.gasProduction || 0,
                BOPDUnit: +unit?.unitBOPDListVal || 0,
                inputvalUnit: +unit?.unitGasProductionListVal || 0,
                GLRUnit: +unit?.unitGLRListVal || 0,
                GORUnit: +unit?.unitGORListVal || 0,
                GasProductionUnit: +unit?.unitGasProductionListVal || 0,
                WaterCut: +fluidInput?.waterCut || 0,
                Productionrate: +tab2Input?.productionRate || 0,
                productionrateUnit: +unit?.unitProductionRateListVal || 0,
                type: 0
            }

            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGOR2(Number(data?.gor)),
                    setGLR2(Number(data?.glr)),
                    { type: 'jetlift/fluidTab/fetchBubblePoint' },
                ]),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            )
        })
    )
};

const gasCalDataGlr = (action$: any, state$: any) => {
    return action$.pipe(
        ofType("jetlift/fluidTab/setGLR"),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.units)),
        ),
        switchMap(([actionData, tab2Input, fluidInput, unit]) => {
            let postData = {
                BOPD: +fluidInput?.bopd || 0,
                inputval: +fluidInput?.glr || 0,
                BOPDUnit: +unit?.unitBOPDListVal || 0,
                inputvalUnit: +unit?.unitGLRListVal || 0,
                GLRUnit: +unit?.unitGLRListVal || 0,
                GORUnit: +unit?.unitGORListVal || 0,
                GasProductionUnit: +unit?.unitGasProductionListVal || 0,
                WaterCut: +fluidInput?.waterCut || 0,
                Productionrate: +tab2Input.productionRate || 0,
                productionrateUnit: +unit?.unitProductionRateListVal || 0,
                type: 2
            }

            return from(getGasCalData(postData)).pipe(
                mergeMap((data) => [
                    setGasProduction2(Number(data?.gasProduction)),
                    setGOR2(Number(data?.gor)),
                    { type: 'jetlift/fluidTab/fetchBubblePoint' },
                ]),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/error', payload: error, error: true });
                })
            )
        })
    );
};

const getGasCompressibilityEpic = (action$: any, state$: any) => {
    return action$.pipe(
        ofType(
            "jetlift/fluidTab/fetchGasCompressibility",
            "jetlift/fluidTab/setBottomHoleTemp",
            "jetlift/fluidTab/setOilAPI",
            "jetlift/fluidTab/setSpGrGas",
            "jetlift/well/setPumpSuctionPressure",
        ),
        withLatestFrom(
            state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
            state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput))
        ),
        switchMap(([actionData, fluidInput, wellInput]) => {
            const gasCompressPayload = {
                reservoirTemp: +fluidInput?.bottomHoleTemp || 0,
                specificGasGravity: +fluidInput?.spGrGas || 0,
                API: +fluidInput?.oilAPI || 0,
                pumpSuctionPressure: +wellInput?.desiredPumpSuctionPressure || 0,
            } as gasCompressPayload;
            return from(getGasCompressValue(gasCompressPayload)).pipe(
                map((response) => {
                    return setGasCompressibility(Number(response));
                }),
                catchError((error) => {
                    console.error(error);
                    return of({ type: 'jetlift/fluidTab/setError', payload: error?.message, error: true });
                })
            );
        })
    );
};

export const jetLiftFluidEpic = combineEpics(
    fetchCombinedSpGrEpic,
    fetchWaterCutChangeOilCut,
    fetchBubblePoint,
    GetOilApiChange,
    GetFluidViscosity,
    GetSpGrOilChanges,
    gasProdChange,
    gasCalDataGor,
    gasCalDataGasProduction,
    gasCalDataGlr,
    getGasCompressibilityEpic,
);
